import { useState } from "react";
import type { FunnelTemplateId } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";

export function TemplateCard({
  templateId,
  content,
  selected,
  recommended,
  deprioritized,
  categoryLabel,
  auditTicket,
  offerAssemblyCtaLabel,
  offerAssemblyCurrentState,
  onSelect,
  onKeyDown,
  tabIndex,
}: {
  templateId: FunnelTemplateId;
  content: FunnelPlannerContent;
  selected: boolean;
  recommended: boolean;
  deprioritized?: boolean;
  categoryLabel?: string;
  auditTicket?: string;
  offerAssemblyCtaLabel?: string;
  offerAssemblyCurrentState?: "untouched_empty" | "active_inprogress" | "partial" | "complete_result" | "reduced_motion_static" | "failure_empty";
  onSelect: (id: FunnelTemplateId) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  tabIndex?: number;
}) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const labelKey = `templates.${templateId}.label` as keyof FunnelPlannerContent;
  const descKey = `templates.${templateId}.description` as keyof FunnelPlannerContent;
  const bestForKey = `templates.${templateId}.bestFor` as keyof FunnelPlannerContent;

  const labelId = `card-label-${templateId}`;
  const descId = `card-desc-${templateId}`;

  const showHighlight = (isHovered || isFocused) && !selected;

  let borderColor = "#334155";
  if (selected) borderColor = "#2563eb";
  else if (recommended && !deprioritized) borderColor = "#2563eb";
  else if (showHighlight) borderColor = "#3b82f6";

  let bgColor = "rgba(255,255,255,0.04)";
  if (selected) bgColor = "rgba(37,99,235,0.12)";
  else if (recommended && !deprioritized) bgColor = "rgba(37,99,235,0.06)";

  const labelColor = selected ? "#93c5fd" : recommended && !deprioritized ? "#bfdbfe" : "#e2e8f0";

  const cardOpacity = deprioritized && !selected ? 0.7 : 1;
  const hasOfferAssemblyCta = Boolean(auditTicket);
  const ctaState = offerAssemblyCurrentState ?? (selected ? "complete_result" : "active_inprogress");
  const ctaLabel = offerAssemblyCtaLabel ?? (content.gamificationOfferAssemblyCtaScopeThis as string);
  const ctaStatus = selected
    ? (content.gamificationOfferAssemblyCtaSelected as string)
    : (content.gamificationOfferAssemblyCtaEmptyStatus as string);

  return (
    <>
      {categoryLabel && (
        <h3
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#94a3b8",
            marginBottom: 6,
            marginTop: 4,
          }}
        >
          {categoryLabel}
        </h3>
      )}
      <button
        type="button"
        role="radio"
        onClick={() => onSelect(templateId)}
        onKeyDown={onKeyDown}
        aria-checked={selected}
        aria-labelledby={labelId}
        aria-describedby={descId}
        tabIndex={tabIndex}
        data-funnel-planner-offer-assembly-cta={hasOfferAssemblyCta ? "true" : undefined}
        data-audit-ticket={auditTicket}
        data-tier-one-family={hasOfferAssemblyCta ? "offer_assembly_mini_cta_brief" : undefined}
        data-gamification-current-state={hasOfferAssemblyCta ? ctaState : undefined}
        data-gamification-reduced-motion-static={hasOfferAssemblyCta ? "supported" : undefined}
        data-gamification-supported-states={
          hasOfferAssemblyCta
            ? "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty"
            : undefined
        }
        data-readiness-ready-count={hasOfferAssemblyCta ? (selected ? 1 : 0) : undefined}
        data-readiness-total-count={hasOfferAssemblyCta ? 1 : undefined}
        data-readiness-tier={hasOfferAssemblyCta ? (selected ? "brief_piece_ready" : "brief_piece_available") : undefined}
        data-blockers-bucket={hasOfferAssemblyCta ? (selected ? "none" : "one") : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className="focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none"
        style={{
          position: "relative",
          width: "100%",
          minHeight: 44,
          borderRadius: 8,
          border: `2px solid ${borderColor}`,
          background: bgColor,
          padding: "14px 16px",
          textAlign: "start",
          cursor: "pointer",
          transition: "border-color 0.15s, background 0.15s",
          opacity: cardOpacity,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <p
            id={labelId}
            style={{
              fontWeight: 600,
              fontSize: 16,
              color: labelColor,
              margin: 0,
              flex: 1,
            }}
          >
            {content[labelKey] as string}
          </p>
          {recommended && (
            <span
              aria-hidden="true"
              data-funnel-planner-recommended-badge="true"
              style={{
                borderRadius: 999,
                background: "rgba(37,99,235,0.20)",
                padding: "2px 8px",
                fontSize: 11,
                fontWeight: 700,
                color: "#93c5fd",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                flexShrink: 0,
                alignSelf: "center",
              }}
            >
              {content.recommendedBadge as string}
            </span>
          )}
        </div>
        <p
          id={descId}
          style={{ fontSize: 14, color: "#cbd5e1", lineHeight: 1.4, margin: 0 }}
        >
          {content[descKey] as string}
        </p>
        <span
          style={{
            display: "inline-block",
            marginTop: 6,
            fontSize: 12,
            color: "#60a5fa",
            fontWeight: 500,
          }}
        >
          {content.bestForPrefix as string}{" "}
          {content[bestForKey] as string}
        </span>
        {hasOfferAssemblyCta && (
          <>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: 10,
                minHeight: 26,
                borderRadius: 999,
                border: selected ? "1px solid rgba(16,185,129,0.48)" : "1px solid rgba(96,165,250,0.38)",
                background: selected ? "rgba(16,185,129,0.14)" : "rgba(37,99,235,0.12)",
                color: selected ? "#a7f3d0" : "#bfdbfe",
                fontSize: 11,
                fontWeight: 800,
                padding: "3px 9px",
              }}
            >
              {ctaLabel}
            </span>
            <span className="sr-only" role="status" aria-live="polite">
              {ctaStatus}
            </span>
          </>
        )}
      </button>
    </>
  );
}
